import React, { CSSProperties, memo, MouseEvent, useCallback, useRef } from 'react';
import { DropTargetMonitor, useDrop } from 'react-dnd';
import cn from 'classnames';
import { isFuture } from 'date-fns';

import { IEvent } from '~/Models/DTO/Event.model';
import {
  DraggableEvent,
  DraggableType,
  getEventTimeInSeconds,
  IGetEventPosition,
} from '~/Screens/Calendar/CalendarView/components';
import { ICalendarDayEvent, ICalendarOffset, IDraggableEvent } from '~/Screens/Calendar/interfaces';
import { useStyles } from './CalendarDay.styles';
import { useCalendarDay } from './useCalendarDay';
import { TIME_VALUES } from '~/Constants/Date.constants';

interface ICalendarDayViewProps {
  events: IEvent[];
  offset: ICalendarOffset;
  position: CSSProperties['position'];
  className?: string;
  daysOffset?: number;
  updateEvent: (item: IDraggableEvent, monitor: DropTargetMonitor) => void;
  onCalendarDayClicked: (newCalendarDayEvent: ICalendarDayEvent) => void;
  getEventPosition: IGetEventPosition;
}

export const CalendarDay = memo(
  ({
    events,
    offset,
    position,
    className,
    daysOffset = 0,
    updateEvent,
    onCalendarDayClicked,
    getEventPosition,
  }: ICalendarDayViewProps) => {
    const { classes } = useStyles();
    const ref = useRef<HTMLDivElement>(null);
    const [, drop] = useDrop(
      () => ({
        accept: DraggableType.EVENT,
        drop: updateEvent,
        canDrop: (item, monitor) => {
          const differenceFromInitialOffset = monitor.getDifferenceFromInitialOffset();
          if (!differenceFromInitialOffset) {
            return false;
          }
          const time = getEventTimeInSeconds(item.event);
          const eventPosition = getEventPosition({ item, differenceFromInitialOffset });
          const start =
            time.dayStart +
            eventPosition.daysOffset * TIME_VALUES.DAY +
            TIME_VALUES.DAY * (eventPosition.top / offset.height);

          return isFuture(start);
        },
      }),
      [updateEvent, getEventPosition],
    );
    const { eventsData } = useCalendarDay({
      daysOffset,
      offset,
      events,
    });
    const onDayClicked = useCallback(
      (event: MouseEvent<HTMLElement>) => {
        onCalendarDayClicked({ event, daysOffset });
      },
      [onCalendarDayClicked, daysOffset],
    );

    return (
      <div
        className={cn(className, classes.dayContainer)}
        ref={drop}
        style={{ position }}
        onClick={onDayClicked}
      >
        <div
          className={classes.dayTimings}
          ref={ref}
          style={{
            height: offset.height,
          }}
        >
          {offset.width &&
            eventsData?.map(({ event, position }) => (
              <DraggableEvent
                key={event.id}
                type={DraggableType.EVENT}
                event={event}
                position={position}
              />
            ))}
        </div>
      </div>
    );
  },
);
